import gsap from 'gsap';
import { BasePreBonusContentDrawer } from './BasePreBonusContentDrawer';
import {
  gameMobileConfig,
  getFontSize,
} from '../../../core/configs';
import { bonusPreBonusSharedDrawer } from '../../bonusPreBonusShared/BonusPreBonusSharedDrawer';
import {
  PRE_BONUS_CRYSTALS_STATE,
  FREE_SPINS_STATE,
  MULTIPLIER_STATE,
  PRE_BONUS_RE_SPINS_LEFT_STATE,
  PRE_BONUS_RESPONSE,
  PRE_BONUS_RESPONSE_ANIMATE,
} from '../../../stateManagment/gameState';
import {
  bonusPreBonusSharedRssNames,
  crystalToDiamond,
  crystalTypes,
  preBonusStoneNames,
  preBonusTextures,
  PreBonusTypes,
  wheelResourceNames,
} from '../../../core/gameConstants';
import { shuffle } from '../../../core/helpers/shuffle';
import { random } from '../../../core/helpers/random';
import { preBonusResponseHelper } from '../../../services/helperService/PreBonusResponseHelper';
import { getTranslation, translationKeys } from '../../../core/translation/translation';
import {
  FONT_BALOO_NAME,
  FONT_LATO_NAME,
} from '../../../core/constats';
import { animationDrawer } from '../../../core/AnimationDrawer';
import { soundService } from '../../../services/helperService/PixiSoundService';

export class PreBonusContentMobileDrawer extends BasePreBonusContentDrawer {
  constructor() {
    super();
    this.container = null;
    this.loader = this.createLoader();
    this.config = {
      ...this.config,
      margin: 4,
      positions: {
        content: {},
      },
    };
  }

  setFreeSpin() {
    const size = (gameMobileConfig.config.width - gameMobileConfig.preBonusContentSize.width) / 2 - 30;
    const [freeSpin, onChange] = bonusPreBonusSharedDrawer.getFreeSpin({
      size,
    });
    const h = size * 3 / 4;

    this.setPosition(freeSpin, { x: 10 + size / 2, y: h - this.config.margin });
    this.addSubscriber(FREE_SPINS_STATE.subscribe(onChange, true));
    this.container.addChild(freeSpin);
    this.freeSpinPosition = { x: 10 + size / 2, y: h - this.config.margin };
  }

  setMultiplier() {
    const size = (gameMobileConfig.config.width - gameMobileConfig.preBonusContentSize.width) / 2 - 30;
    const [multiplier, onChange] = bonusPreBonusSharedDrawer.getMultiplier({
      size: size,
    });
    const h = size * 3 / 4;

    const x = 10 + size / 2;
    const y = gameMobileConfig.preBonusContentSize.height - h + this.config.margin;

    this.setPosition(multiplier, { x, y });
    this.addSubscriber(MULTIPLIER_STATE.subscribe(onChange, true));
    this.container.addChild(multiplier);
    this.multiplierPosition = {
      x,
      y,
    };
  }

  setCrystalsContent() {
    const size = (gameMobileConfig.config.width - gameMobileConfig.preBonusContentSize.width) / 2 - 30;
    const crystalSize = size * 4 / 15;
    const [container, onChange, crystals] = bonusPreBonusSharedDrawer.getCrystalsContentX3_Big({
      crystals: [
        {
          name: bonusPreBonusSharedRssNames.crystalYellow,
          observerName: crystalTypes.crystal_yellow,
          size: crystalSize,
        },
        {
          name: bonusPreBonusSharedRssNames.crystalGreen,
          observerName: crystalTypes.crystal_green,
          size: crystalSize,
        },
        {
          name: bonusPreBonusSharedRssNames.crystalMagenta,
          observerName: crystalTypes.crystal_magenta,
          size: crystalSize,
        },
      ],
      size,
    });
    this.addSubscriber(PRE_BONUS_CRYSTALS_STATE.subscribe(onChange, true));
    const y = gameMobileConfig.preBonusContentSize.height / 2;
    const x = gameMobileConfig.config.width - (size / 2 + 10);
    this.setPosition(container, { x, y });
    this.container.addChild(container);

    this.crystalsPosition = crystals.reduce((acc, item) => {
      acc[item.name] = { x: x + item.position.x - crystalSize, y: y + item.position.y };
      return acc;
    }, {});
  }

  getPreBonusElements({
    size = 200,
    sizeDifference = 20,
    parentSize = gameMobileConfig.preBonusContentSize.width,
    xSize = [parentSize / 6, parentSize / 2, parentSize * 5 / 6],
    ySize = [parentSize / 6 + 5, parentSize / 2, parentSize * 5 / 6],
    container = this.createContainer(),
  }) {
    const masks = [];
    const elements = new Array(9).fill(null).map((v, index) => {
      const x = xSize[index % 3];
      const y = ySize[index < 3 ? 0 : index < 6 ? 1 : 2];
      const rectContainer = this.createContainer(index);
      const s = (index % 2 === 0 ? size : size - sizeDifference) - 10;
      const c = index % 2 === 0 ? 0x00FF00 : 0xFF0000;
      const rect = this.createGraphics()
        .beginFill(c)
        .drawRect(
          -s / 2,
          -s / 2,
          s,
          s,
        );
      masks.push(rect);
      rectContainer.mask = rect;
      rect.alpha = 0;
      rectContainer.addChild(rect);
      container.addChild(rectContainer);
      this.setPosition(rectContainer, { x, y });
      this.setSizes(rectContainer, { w: s, h: s });
      rectContainer.size = s;
      return rectContainer;
    });

    return [container, elements, () => {
      elements.forEach((e, i) => e.mask = masks[i]);
    }];
  }

  getTextures() {
    return shuffle(preBonusTextures).reduce((acc, name, index) => {
      if (!index) {
        acc.push(...new Array(random(1, 5)).fill('opacity'));
      }
      acc.push(name);
      acc.push(...new Array(random(1, 5)).fill('opacity'));
      return acc;
    }, []);
  }

  getElements({ size = 200 } = {}) {
    const textures = this.getTextures();
    let img = this.createSprite(this.resources[preBonusStoneNames.opacity]);
    return textures.map((name) => {
      let text = null;
      if (name === PreBonusTypes.freeSpin || name === PreBonusTypes.multiplier) {
        img = this.createSprite(this.resources[preBonusStoneNames.opacity]);
      } else {
        if (name !== 'opacity') {
          text = new this.PIXI.Text('', this.config.style[name]);
        }
        img = this.createSprite(this.resources[preBonusStoneNames[name]]);
      }
      img.name = name;
      img.anchor.set(.5);
      img.scale.set(size / img.width);
      if (text) {
        text.anchor.set(.5);
        const container = this.createContainer(name);
        container.addChild(
          img,
          text,
        );
        return container;
      }
      return img;
    });
  }

  setupAnimation({ root, elements, index, size, duration = .15, id }) {
    const prevIndex = index === elements.length - 1 ? 0 : index + 1;
    const el = elements[index];
    const prev = elements[prevIndex];
    const [contentM, startM] = bonusPreBonusSharedDrawer.getPreBonusLightEffect({
      size: root.size,
      name: bonusPreBonusSharedRssNames.preBonus_light_effect,
    });
    const [contentC, startC] = bonusPreBonusSharedDrawer.getPreBonusLightEffect({
      size: root.size,
      name: bonusPreBonusSharedRssNames.pre_bonus_crystal_light_effect,
    });
    const [contentF, startF] = bonusPreBonusSharedDrawer.getPreBonusLightEffect({
      size: root.size,
      name: bonusPreBonusSharedRssNames.pre_bonus_free_spin_light_effect,
    });
    const sizes = [
      getFontSize(.1),
      getFontSize(.085),
      getFontSize(.065),
      getFontSize(.04),
    ];
    const prevText = new this.PIXI.Text('XX', {
      ...bonusPreBonusSharedDrawer.config.crystalTextStyle,
      fontSize: getFontSize(.1),
    });

    prevText.anchor.set(.5);
    prevText.visible = false;
    if (
      preBonusResponseHelper.isNull()
      || preBonusResponseHelper.hasMultiplier()
      || preBonusResponseHelper.hasFreeSpins()
      || preBonusResponseHelper.hasCrystalInfo()
    ) {
      switch (prev.name) {
        case PreBonusTypes.multiplier:
          prevText.text = `X${preBonusResponseHelper.hasMultiplier()
              ? preBonusResponseHelper.multiplier
              : random(1, 15)
            }`;

          prevText.style = {
            ...bonusPreBonusSharedDrawer.config.multiplierStyle,
            fontSize: sizes[prevText.text.length - 1],
            fontFamily: FONT_BALOO_NAME,
            stroke: '#6e00fa',
            strokeThickness: 4,
          };
          prevText.visible = true;
          break;
        case PreBonusTypes.freeSpin:
          prevText.text = preBonusResponseHelper.hasFreeSpins() ? preBonusResponseHelper.freeSpins : random(1, 10);
          prevText.style = {
            ...bonusPreBonusSharedDrawer.config.freeSpinsStyle,
            fontSize: sizes[prevText.text.length - 1],
            fontFamily: FONT_BALOO_NAME,
            stroke: '#00606d',
            strokeThickness: 4,
          };
          prevText.visible = true;
          break;
        case PreBonusTypes.greenCrystal:
        case PreBonusTypes.magentaCrystal:
        case PreBonusTypes.yellowCrystal:
          const crystalCount = `x${preBonusResponseHelper.hasCrystalInfo() && crystalToDiamond[preBonusResponseHelper.crystalInfo.crystal] === prev.name
              ? preBonusResponseHelper.crystalInfo.count
              : random(1, 5)
            }`;
          prev.children[1].text = crystalCount;
          break;
        default:
          break;
      }
    }

    const oldText = root.children[5] || new this.PIXI.Text('');
    this.setPosition(contentM, { y: root.size / 2 });
    this.setPosition(contentC, { y: root.size / 2 });
    this.setPosition(contentF, { y: root.size / 2 });
    this.setPosition(el, { x: 0, y: 0 });
    this.setPosition(oldText, { x: 0, y: 0 });
    this.setPosition(prev, { x: 0, y: -prev.height });
    this.setPosition(prevText, { x: 0, y: -prev.height });

    root.children = [];
    root.addChild(
      contentM,
      contentC,
      contentF,
      el,
      prev,
      prevText,
      oldText,
    );

    gsap.to(el, { duration: duration, ease: 'none', y: el.height });
    gsap.to(oldText, { duration: duration, ease: 'none', y: el.height });
    gsap.to(prevText, { duration: duration, ease: 'none', y: 0 });
    gsap.to(prev, {
      duration: duration,
      ease: 'none',
      y: 0,
      onComplete: () => {
        const preBonusAnimate = PRE_BONUS_RESPONSE_ANIMATE.getValue();
        const preBonusResponse = PRE_BONUS_RESPONSE.getValue();
        if (preBonusAnimate.has(id)) {
          if (preBonusResponse !== null) {
            if (prev.name !== 'opacity') {
              let cX, cY;

              switch (prev.name) {
                case PreBonusTypes.multiplier:
                  cX = this.multiplierPosition.x;
                  cY = this.multiplierPosition.y;
                  break;
                case PreBonusTypes.freeSpin:
                  cX = this.freeSpinPosition.x;
                  cY = this.freeSpinPosition.y;
                  break;
                default:
                  if (prev.name === crystalToDiamond[preBonusResponse.crystalInfo.crystal]) {
                    cX = this.crystalsPosition[preBonusStoneNames[prev.name]].x;
                    cY = this.crystalsPosition[preBonusStoneNames[prev.name]].y;
                  }
                  break;
              }
              const { x: eX, y: eY } = this.calculatePosition({
                element: prev,
                containerName: this.container.name,
              });

              const x = cX - eX;
              const y = cY - eY;

              let target = null;
              let animationType = null;
              let count = null;

              if (preBonusResponseHelper.hasFreeSpins() || preBonusResponseHelper.hasMultiplier() || preBonusResponseHelper.hasCrystalInfo()) {
                if (
                  prev.name === PreBonusTypes.multiplier
                  && preBonusResponseHelper.hasMultiplier()
                  && preBonusResponseHelper.multiplier === Number(prevText.text.slice(1))
                ) {
                  count = preBonusResponseHelper.multiplier;
                  preBonusResponseHelper.nullifyMultiplier();
                  target = prevText;
                  animationType = PreBonusTypes.multiplier;
                } else if (
                  prev.name === PreBonusTypes.freeSpin
                  && preBonusResponseHelper.hasFreeSpins()
                  && preBonusResponseHelper.freeSpins === Number(prevText.text)
                ) {
                  count = preBonusResponseHelper.freeSpins;
                  preBonusResponseHelper.nullifyFreeSpins();
                  target = prevText;
                  animationType = PreBonusTypes.freeSpin;
                } else if (prev.name === crystalToDiamond[preBonusResponse.crystalInfo.crystal] && preBonusResponseHelper.hasCrystalInfo()) {
                  if (preBonusResponseHelper.crystalInfo.count === Number(prev.children[1].text.slice(1))) {
                    target = prev;
                    animationType = preBonusResponse.crystalInfo.crystal;
                    count = preBonusResponse.crystalInfo.count;
                    preBonusResponseHelper.nullifyCrystalInfo();
                  }
                }
                if (
                  [
                    PreBonusTypes.multiplier,
                    PreBonusTypes.freeSpin,
                    crystalToDiamond[preBonusResponse.crystalInfo.crystal],
                  ].includes(prev.name) && target
                ) {
                  el.alpha = 0;
                  if (root.mask) {
                    root.mask = null;
                  }
                  const newAnimationState = new Set(preBonusAnimate);
                  newAnimationState.delete(id);
                  PRE_BONUS_RESPONSE_ANIMATE.next(newAnimationState);

                  switch (animationType) {
                    case PreBonusTypes.multiplier:
                      startM();
                      break;
                    case PreBonusTypes.freeSpin:
                      startF();
                      break;
                    case crystalTypes.crystal_green:
                    case crystalTypes.crystal_magenta:
                    case crystalTypes.crystal_blue:
                    case crystalTypes.crystal_yellow:
                      startC();
                      break;
                    default:
                      break;
                  }

                  soundService.playMoveSound();
                  gsap.to(target, {
                    onComplete: () => {
                      switch (animationType) {
                        case PreBonusTypes.multiplier:
                          MULTIPLIER_STATE.next(MULTIPLIER_STATE.getValue() + count);
                          break;
                        case PreBonusTypes.freeSpin:
                          FREE_SPINS_STATE.next(FREE_SPINS_STATE.getValue() + count);
                          break;
                        case crystalTypes.crystal_green:
                        case crystalTypes.crystal_magenta:
                        case crystalTypes.crystal_blue:
                        case crystalTypes.crystal_yellow:
                          PRE_BONUS_CRYSTALS_STATE.next({
                            ...PRE_BONUS_CRYSTALS_STATE.getValue(),
                            [animationType]: PRE_BONUS_CRYSTALS_STATE.getValue()[animationType] + count,
                          });
                          break;
                        default:
                          break;
                      }
                      target.alpha = 0;
                    },
                    delay: 1,
                    duration: duration,
                    ease: 'none',
                    x,
                    y,
                    pixi: {
                      scale: .2,
                      alpha: 1,
                    },
                  });
                  return;
                }
              }
            } else if (!(preBonusResponseHelper.hasFreeSpins() || preBonusResponseHelper.hasMultiplier() || preBonusResponseHelper.hasCrystalInfo())) {
              const newAnimationState = new Set(preBonusAnimate);
              newAnimationState.delete(id);
              PRE_BONUS_RESPONSE_ANIMATE.next(newAnimationState);
              return;
            }
          } else {
            if (prev.name === 'opacity' && preBonusResponseHelper.hasError) {
              const newAnimationState = new Set(preBonusAnimate);
              newAnimationState.delete(id);
              PRE_BONUS_RESPONSE_ANIMATE.next(newAnimationState);
              return;
            }
          }
          this.setupAnimation({ root, elements, index: prevIndex, size, id });
        }
      },
    });
  }

  setPreBonusContainer() {
    const parent = this.createContainer();
    const container = this.createContainer('preBonusContent');

    this.preBonusContainer = parent;

    const rect = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(
        -gameMobileConfig.preBonusContentSize.width / 2,
        -gameMobileConfig.preBonusContentSize.width / 2,
        gameMobileConfig.preBonusContentSize.width,
        gameMobileConfig.preBonusContentSize.height,
      );
    rect.alpha = 0;

    const [elementsContainer, elements, resetMask] = this.getPreBonusElements({
      size: gameMobileConfig.preBonusContentSize.width / 3,
    });

    this.elements = elements;
    this.resetMask = resetMask;

    const [content, goldContent] = this.getPreBonusContent({
      percent: 76,
      size: gameMobileConfig.preBonusContentSize.width,
    });

    this.setPosition(parent, {
      x: gameMobileConfig.config.width / 2,
      y: gameMobileConfig.preBonusContentSize.height / 2,
    });

    this.setPosition(elementsContainer, {
      x: -gameMobileConfig.preBonusContentSize.width / 2,
      y: -gameMobileConfig.preBonusContentSize.width / 2,
    });

    container.addChild(
      content,
      rect,
      elementsContainer,
      goldContent,
    );


    parent.addChild(
      container,
    );

    this.container.addChild(
      parent,
    );
  }

  setupContainerPosition() {
    this.setPosition(this.container, gameMobileConfig.preBonusContentContainerPosition);
  }

  setReSpinLeft() {
    this.textContainer = bonusPreBonusSharedDrawer.getTextContainer();
    const reSpinText = new this.PIXI.Text(`${getTranslation(translationKeys.reSpinsLeft)}  `, {
      fill: [0xfefefd, 0x9b7853],
      fontFamily: FONT_LATO_NAME,
      stroke: '#dec195',
      strokeThickness: 1,
      fontSize: 45,
      align: 'center',
    });
    reSpinText.anchor.set(.5);
    this.addSubscriber(PRE_BONUS_RE_SPINS_LEFT_STATE.subscribe((num, { prev }) => {
      if (prev !== null && num !== prev) {
        animationDrawer.blinking(reSpinText, .2, [1, .85, 1]);
      }
      reSpinText.text = `${getTranslation(translationKeys.reSpinsLeft)} ${num}`;
    }, true));
    this.setPosition(this.textContainer,
      {
        x: gameMobileConfig.config.width / 2,
        y: gameMobileConfig.preBonusContentSize.height + gameMobileConfig.config.height * .06,
      });

    this.textContainer.addChild(
      reSpinText,
    );
    this.container.addChild(
      this.textContainer,
    );
  }

  loadPreBonusAnimation() {
    this.resetMask();
    this.elements.forEach((parent, id) => {
      this.setupAnimation({
        root: parent,
        elements: this.getElements({ size: gameMobileConfig.preBonusContentSize.width / 3 }),
        index: 0,
        id,
      });
    });
  }

  screenTransaction() {
    this.textContainer.alpha = 0;
    const texture = this.createSpine(this.resources[wheelResourceNames.preBonusTransaction]);
    texture.state.setAnimation(0, 'animation', false);
    this.preBonusContainer.addChild(texture);
    animationDrawer.shake(this.preBonusContainer, .1, this.preBonusContainer.position);
  }

  async onLoad() {
    bonusPreBonusSharedDrawer.resetStyles();
    await bonusPreBonusSharedDrawer.loadResource();
    await super.loadResource();
  }

  async executePreBonusScene() {
    bonusPreBonusSharedDrawer.resetStyles();
    await bonusPreBonusSharedDrawer.loadResource();
    await super.loadResource();
    this.setCrystalsContent();
    this.setupContainerPosition();
    this.setFreeSpin();
    this.setMultiplier();
    this.setReSpinLeft();
    this.setPreBonusContainer();
  }
}

